// =============================================================================
// Shared address-validation helpers (Google Address Validation, USPS-backed).
//
// Two places call /api/AddressValidation/validate:
//   1. when the user picks a Google autocomplete suggestion — so the
//      standardized address lands in the fields straight away instead of being
//      rewritten under the user at submit time;
//   2. at submit, as the authority.
//
// Google Places returns the long form ("123 William Street"); validation
// returns it USPS-standardized ("123 WILLIAM ST"). That difference is a
// reformat, not a correction, and it used to fire a "the address was corrected"
// prompt on nearly every address picked from autocomplete.
//
// Results are memoized by address so pick-then-submit costs one billed call,
// not two.
// =============================================================================

const CACHE_LIMIT = 100;
const cache = new Map();

const norm = (v) => String(v ?? "").trim().toUpperCase();

export const addressKey = (a) =>
  [a.address1, a.city, a.state, a.zipCode].map(norm).join("|");

const remember = (key, value) => {
  // Cheap FIFO cap — this lives for the lifetime of the tab and only ever
  // holds addresses the user actually touched, but don't let it grow forever.
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value);
  cache.set(key, value);
};

/** Cached result for this address, or undefined if it hasn't been validated. */
export const peekValidation = (input) => cache.get(addressKey(input));

/**
 * Store a validation result. Callers that do their own fetch (because they
 * need the raw error response) use this to populate the cache.
 */
export function rememberValidation(input, v) {
  if (!v) return;
  remember(addressKey(input), v);

  // The standardized address validates to itself, so seed that key too: it's
  // what the submit-time check looks up once the fields have been rewritten
  // to the standardized form.
  if (v.isValid !== false) {
    remember(
      addressKey({ address1: v.address1, city: v.city, state: v.state, zipCode: v.zipCode }),
      v
    );
  }
}

/**
 * POST the address to the validation API. Returns the parsed result, or null
 * when the service is unreachable or errors — callers treat null as "don't
 * block the user".
 */
export async function validateAddress(apiUrl, input) {
  const hit = peekValidation(input);
  if (hit) return hit;

  try {
    const r = await fetch(`${apiUrl}/api/AddressValidation/validate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        address1: input.address1 || "",
        address2: input.address2 || "",
        city: input.city || "",
        state: input.state || "",
        zipCode: input.zipCode || "",
        country: input.country || "US",
      }),
    });
    if (!r.ok) return null;

    const v = await r.json();
    rememberValidation(input, v);
    return v;
  } catch {
    return null;
  }
}

/**
 * True when validation only restyled the street line — "123 William Street"
 * became "123 WILLIAM ST".
 *
 * City / state / ZIP moving is NOT cosmetic. Google resolves a conflicting
 * address by trusting the ZIP, so "123 William Street, Los Angeles CA 10038"
 * comes back fully confirmed as "NEW YORK NY". Silently rewriting the town the
 * user typed would hide a real discrepancy.
 */
export function onlyStreetDiffers(corrected, input) {
  return (
    norm(corrected.city) === norm(input.city) &&
    norm(corrected.state) === norm(input.state) &&
    String(corrected.zipCode || "").slice(0, 5) === String(input.zipCode || "").slice(0, 5)
  );
}

/** Pull the four comparable fields out of a validation response. */
export const correctedFrom = (v) => ({
  address1: v?.address1 || "",
  city: v?.city || "",
  state: v?.state || "",
  zipCode: v?.zipCode || "",
});

/**
 * A confident, purely cosmetic result can be adopted without asking. Anything
 * unconfirmed, invalid, or that moved the city/state/ZIP goes to the prompt.
 */
export function isSafeToAdopt(v, input) {
  if (!v || v.isValid === false || v.hasUnconfirmedComponents) return false;
  return onlyStreetDiffers(correctedFrom(v), input);
}
